import { useEffect, useState } from "react";
import api from "@/lib/api";
import TitleBlock from "@/components/TitleBlock";
import Callout from "@/components/Callout";
import { fmtNumber } from "@/lib/format";
import { RefreshCw, ExternalLink, TrendingUp, Activity } from "lucide-react";

function daysAgo(iso) {
  if (!iso) return null;
  return Math.floor((new Date() - new Date(iso)) / (1000 * 60 * 60 * 24));
}

function CompetitorCard({ competitor, onSelect, isSelected }) {
  const baselining = competitor.is_baselining;
  const new7 = competitor.new_7d || 0;
  const new30 = competitor.new_30d || 0;
  const heat = baselining ? "navy" : new7 >= 5 ? "evergreen" : new7 >= 2 ? "cobalt" : "navy";
  const heatColor = { evergreen: "#006747", cobalt: "#1D57A5", navy: "#141B4D" }[heat];
  return (
    <button onClick={() => onSelect(competitor.id)}
            data-testid={`competitor-card-${competitor.id}`}
            className={`text-left bg-warm-tint p-5 border-t-4 hover:bg-warm transition ${isSelected ? "ring-2 ring-yellow" : ""}`}
            style={{ borderTopColor: heatColor }}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-bold text-navy">{competitor.name}</h3>
        <a href={`https://${competitor.domain}`} target="_blank" rel="noreferrer"
           onClick={(e) => e.stopPropagation()}
           className="text-xs text-cobalt hover:underline inline-flex items-center gap-1">
          {competitor.domain} <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
        <div>
          <div className="opacity-60 uppercase tracking-widest text-[10px]">Pages indexed</div>
          <div className="text-lg font-bold text-navy">{fmtNumber(competitor.latest?.url_count || 0)}</div>
        </div>
        <div>
          <div className="opacity-60 uppercase tracking-widest text-[10px]">New · 7d</div>
          <div className="text-lg font-bold" style={{ color: heatColor }}>
            {baselining ? "—" : new7}
          </div>
        </div>
        <div>
          <div className="opacity-60 uppercase tracking-widest text-[10px]">New · 30d</div>
          <div className="text-lg font-bold text-navy">{baselining ? "—" : new30}</div>
        </div>
      </div>
      {baselining && (
        <div className="mt-3 text-[10px] uppercase tracking-widest text-yellow bg-navy px-2 py-1 inline-block">
          Baselining · diff starts tomorrow
        </div>
      )}
    </button>
  );
}

export default function SeoCompetitorsPage() {
  const [competitors, setCompetitors] = useState([]);
  const [sync, setSync] = useState(null);
  const [selected, setSelected] = useState(null);
  const [feed, setFeed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [windowDays, setWindowDays] = useState(30);

  const loadList = () => api.get("/seo/competitors").then((r) => {
    setCompetitors(r.data.competitors);
    setSync(r.data.sync);
    if (!selected && r.data.competitors[0]) setSelected(r.data.competitors[0].id);
  });
  useEffect(() => { loadList(); }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/seo/competitors/${selected}/feed?days=${windowDays}`).then((r) => setFeed(r.data));
  }, [selected, windowDays]);

  const runSync = async () => {
    setBusy(true);
    try { await api.post("/seo/competitors/sync"); await loadList(); }
    finally { setBusy(false); }
  };

  const current = competitors.find((c) => c.id === selected);
  return (
    <div data-testid="seo-competitors-page">
      <TitleBlock
        eyebrow="SEO · daily competitor watch"
        title="Competitor Watch"
        subtitle="Every morning at 6 AM UTC we pull each competitor's sitemap and diff it against yesterday. New pages are surfaced here — react same-day instead of finding out two weeks later in Ahrefs."
        right={
          <button onClick={runSync} disabled={busy} data-testid="competitors-sync"
                  className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            Refresh now
          </button>
        }
      />
      <div className="p-6 md:p-8 space-y-6">
        <Callout title="How to use this">
          <div className="text-sm text-midnight">
            Each card below shows a competitor&apos;s total indexed pages plus how many they&apos;ve added in the last 7 / 30 days. <strong>Click a card</strong> to see the per-URL activity feed below — useful when Josh Gerben publishes breaking trademark news and you want to react with a same-day follow-up. We track first-seen-date per URL so you only see truly new content, not re-shuffled sitemaps.
          </div>
          {sync?.last_synced && (
            <div className="text-[10px] mt-2 opacity-60">Last sitemap sync: {new Date(sync.last_synced).toLocaleString()}</div>
          )}
        </Callout>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {competitors.map((c) => (
            <CompetitorCard key={c.id} competitor={c} onSelect={setSelected} isSelected={c.id === selected} />
          ))}
        </div>

        {current && (
          <div className="bg-warm-tint border-t-4 border-navy p-6">
            <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
              <h2 className="text-xl font-bold text-navy inline-flex items-center gap-2">
                <Activity className="w-5 h-5 text-yellow" />
                {current.name} · Activity Feed
              </h2>
              <div className="flex gap-2">
                {[7, 30, 90].map((d) => (
                  <button key={d} onClick={() => setWindowDays(d)} data-testid={`window-${d}`}
                          className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${windowDays === d ? "bg-yellow text-midnight border-yellow" : "border-yellow text-yellow hover:bg-yellow hover:text-midnight"}`}>
                    {d}d
                  </button>
                ))}
              </div>
            </div>

            <h3 className="text-sm font-bold text-evergreen uppercase tracking-widest mb-3">
              <TrendingUp className="w-3.5 h-3.5 inline mr-1" /> New pages in last {windowDays}d ({current?.is_baselining ? "—" : (feed?.events?.length || 0)})
            </h3>
            <div className="space-y-2">
              {current?.is_baselining ? (
                <div className="bg-warm border-l-4 border-yellow p-4 text-sm" data-testid="baselining-banner">
                  <strong className="text-navy">Baselining in progress.</strong>{" "}
                  <span className="text-midnight opacity-80">
                    We just took the <em>first</em> snapshot of <strong>{current.name}</strong> — {fmtNumber(current.latest?.url_count || 0)} pages indexed. We can&apos;t tell you what&apos;s genuinely <em>new</em> until we have a second snapshot to diff against. The next sync runs automatically at 6 AM UTC tomorrow — by Wednesday morning this feed will show only truly new content (and the noise you saw earlier will disappear).
                  </span>
                  <div className="mt-2 text-[10px] uppercase tracking-widest opacity-60">
                    Tip: hitting &ldquo;Refresh now&rdquo; before tomorrow&apos;s sync just re-takes the baseline — it won&apos;t generate diff data until at least 24 hours have passed.
                  </div>
                </div>
              ) : (feed?.events || []).slice(0, 50).map((e) => {
                const age = daysAgo(e.first_seen_at);
                const path = (() => { try { return new URL(e.url).pathname; } catch { return e.url; } })();
                return (
                  <div key={e.url} className="flex items-baseline gap-3 text-sm py-1.5 border-b border-warm">
                    <span className="text-[10px] opacity-50 w-12 shrink-0">{age === 0 ? "today" : age === 1 ? "yesterday" : `${age}d ago`}</span>
                    <a href={e.url} target="_blank" rel="noreferrer"
                       className="text-navy hover:text-cobalt hover:underline flex-1 truncate"
                       title={e.url}>
                      {path}
                    </a>
                    <ExternalLink className="w-3 h-3 opacity-40 shrink-0" />
                  </div>
                );
              })}
              {!current?.is_baselining && (feed?.events || []).length === 0 && (
                <div className="text-xs opacity-50 italic py-6 text-center">
                  No new pages detected in this window — competitor hasn&apos;t published anything tracked here.
                </div>
              )}
            </div>

            {feed?.removed_last_run?.length > 0 && (
              <>
                <h3 className="text-sm font-bold text-red-700 uppercase tracking-widest mt-6 mb-3">
                  Removed since last run ({feed.removed_last_run.length})
                </h3>
                <div className="space-y-1">
                  {feed.removed_last_run.map((u) => (
                    <div key={u} className="text-xs text-midnight opacity-70 line-through">{u}</div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
